'use client'

import React, { useRef, useEffect, useState } from 'react'

interface FlowerOfLifeProps {
  maxSize?: number
  color?: string
  speed?: string
}

export default function FlowerOfLife({ maxSize = 300, color = "white", speed = "6s" }: FlowerOfLifeProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState(0)

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const containerSize = Math.min(
          containerRef.current.offsetWidth,
          containerRef.current.offsetHeight,
          maxSize
        )
        setSize(containerSize)
      }
    }

    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [maxSize])

  const baseUnit = size / 6

  return (
    <div ref={containerRef} className="w-full h-full flex items-center justify-center">
      {size > 0 && (
        <div className="relative">
          <svg
            width={size}
            height={size}
            viewBox={`${-size/2} ${-size/2} ${size} ${size}`}
            className="max-w-full max-h-full"
            aria-label="Animated Flower of Life pattern with expanding encapsulating circle"
          >
            {/* Encapsulating circle */}
            <circle
              cx="0"
              cy="0"
              r={baseUnit}
              fill="none"
              stroke={color}
              strokeWidth="0.5"
              style={{
                animation: `container-expand ${speed} infinite ease-in-out`
              }}
            />

            {/* Center circle */}
            <circle
              cx="0"
              cy="0"
              r={baseUnit}
              fill="none"
              stroke={color}
              strokeWidth="1"
              style={{
                animation: `expand ${speed} infinite ease-in-out`,
                '--tx': '0px',
                '--ty': '0px'
              } as React.CSSProperties}
            />

            {/* First ring */}
            {[
              { tx: 0, ty: -1 },
              { tx: 0.866, ty: -0.5 },
              { tx: 0.866, ty: 0.5 },
              { tx: 0, ty: 1 },
              { tx: -0.866, ty: 0.5 },
              { tx: -0.866, ty: -0.5 }
            ].map((pos, i) => (
              <circle
                key={`first-${i}`}
                cx="0"
                cy="0"
                r={baseUnit}
                fill="none"
                stroke={color}
                strokeWidth="1"
                style={{
                  animation: `expand ${speed} infinite ease-in-out`,
                  '--tx': `${pos.tx * baseUnit}px`,
                  '--ty': `${pos.ty * baseUnit}px`
                } as React.CSSProperties}
              />
            ))}

            {/* Second ring */}
            {[
              { tx: 0, ty: -2 },
              { tx: 0.866, ty: -1.5 },
              { tx: 1.732, ty: -1 },
              { tx: 1.732, ty: 0 },
              { tx: 1.732, ty: 1 },
              { tx: 0.866, ty: 1.5 },
              { tx: 0, ty: 2 },
              { tx: -0.866, ty: 1.5 },
              { tx: -1.732, ty: 1 },
              { tx: -1.732, ty: 0 },
              { tx: -1.732, ty: -1 },
              { tx: -0.866, ty: -1.5 }
            ].map((pos, i) => (
              <circle
                key={`second-${i}`}
                cx="0"
                cy="0"
                r={baseUnit}
                fill="none"
                stroke={color}
                strokeWidth="1"
                style={{
                  animation: `expand ${speed} infinite ease-in-out`,
                  '--tx': `${pos.tx * baseUnit}px`,
                  '--ty': `${pos.ty * baseUnit}px`
                } as React.CSSProperties}
              />
            ))}
          </svg>

          <style jsx>{`
            @keyframes expand {
              0%, 100% { transform: translate(0, 0); }
              50% { transform: translate(var(--tx), var(--ty)); }
            }
            @keyframes container-expand {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(3); }
            }
          `}</style>
        </div>
      )}
    </div>
  )
}